import { appConfig } from 'appConfig';
import React, { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createEstimateFromTemplate,
  useEstimateFromTemplateFieldConfig,
} from 'queries/estimates';
import toast from 'react-hot-toast';
import { actionLabel, readAxiosErr } from 'utils/misc';
import Button from 'components/Button';
import Icon from 'components/Icon';
import { notifyJobStatsRefresh, notifyExpenseCreate } from 'utils/iframe';
import useAuth from 'hooks/useAuth';
import TemplateForm, { IFormValues, TemplateFormRef } from './Form';
import { useJobDetail } from 'queries/job';

type AppProps = {
  goBack: () => void;
  closeModal?: () => void;
  redirectTo?: 'estimates' | 'estimate-detail';
};

const ManualCreate: React.FC<AppProps> = ({
  goBack,
  closeModal,
  redirectTo = 'estimate-detail',
}) => {
  const { jobId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: jobDetail } = useJobDetail(jobId);
  const { data: config, isLoading } = useEstimateFromTemplateFieldConfig(jobId);
  const { embedMode } = useAuth();
  const formRef = useRef<TemplateFormRef>(null);

  const createMutation = useMutation(
    (formData: any) => createEstimateFromTemplate(jobId, formData),
    {
      onError: (err: any) => {
        toast.error(readAxiosErr(err));
      },
      onSuccess: (res: any) => {
        queryClient.invalidateQueries(['job-workorders', { job: jobId }]);
        queryClient.invalidateQueries(['job-matorders', { job: jobId }]);
        queryClient.invalidateQueries(['estimates']);
        queryClient.invalidateQueries(['job-detail', jobId]);
        queryClient.invalidateQueries(['job-stats', jobId]);

        if (embedMode) {
          notifyJobStatsRefresh(jobId);
          notifyExpenseCreate(jobId);
        }

        if (embedMode && window.top) {
          if (redirectTo === 'estimates') {
            window.top.location = `${appConfig.legacyUrl}/jobs/${jobId}/estimateOptions`;
          } else {
            window.top.location = `${appConfig.legacyUrl}/jobs/${jobId}/estimates/${res.id}`;
          }
        } else {
          closeModal?.();
          if (redirectTo === 'estimates') {
            return navigate(`/jobs/${jobId}/estimates`);
          } else {
            return navigate(`/jobs/${jobId}/estimates/${res.id}`);
          }
        }
      },
    }
  );

  const onSubmit = (formData: IFormValues) => {
    createMutation.mutate(formData);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    formRef.current?.handleSubmit(onSubmit)(e);
  };

  if (isLoading) {
    return <div className="center h-40">Fetching form config...</div>;
  }

  return (
    <div className="pb-4">
      <div className="flex justify-between mb-6">
        <div className="text-lg font-bold">Create Estimate from Templates</div>
        {!!closeModal && !createMutation.isLoading && (
          <div>
            <Icon name="close" size={30} onClick={closeModal} />
          </div>
        )}
      </div>
      <form onSubmit={handleFormSubmit}>
        <TemplateForm
          ref={formRef}
          jobId={jobId}
          config={config}
          loading={createMutation.isLoading}
          initialRep={jobDetail?.customer?.rep?.id}
          initialBidType={jobDetail?.bid_type}
        />
        <Button
          label={actionLabel(null, 'Estimate', createMutation.isLoading)}
          className="mt-8"
          loading={createMutation.isLoading}
        />
      </form>
      {!createMutation.isLoading && (
        <div className="center mt-4 cursor-pointer" onClick={goBack}>
          <Icon name="arrow-left" size={16} />
          <div className="text-sm text-text ml-2">Select another option</div>
        </div>
      )}
    </div>
  );
};

export default ManualCreate;
